import React, { FC, useEffect } from "react";

import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import { ActivitiesPanel } from "@/components/features/Activities";
import { StandardLayout } from "@/components/layouts";
import { InsurerLinkPanel } from "@/components/molecules";
import { DetailsPage } from "@/components/templates";
import { contractApi } from "@/shared/apis";
import { ContractDetail, ContractDetailPageState, Permissions } from "@/shared/domain";
import { useAuth, useBreadcrumb, useNavigation, useRouterHook } from "@/shared/hooks";
import { getInsurerLinks, InsurerLink } from "@/shared/utils";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import { RelatedClaims } from "../../Claims";
import ContractItem from "../components/ContractItem/ContractItem";
import ContractTabDetail from "./TabPanels/ContractTabDetail";

const ALL_CONTRACTS_ID = "0";

const ContractDetailPage: FC = () => {
    const {
        getStateOrSearch,
        state: { categoryCode, stateCode },
    } = useRouterHook<ContractDetailPageState>();

    const { user } = useAuth();
    const { setPageTitle } = useBreadcrumb();
    const { id: contractId } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const { incUsageCount } = useUserFeedback();

    const { navToRelatedDocuments, navToContextualConversation, navToRelatedActivities, navToContracts } =
        useNavigation();

    const policyholderId = getStateOrSearch("policyholderId");

    let ibpAclId = user.getIbpAclId(policyholderId);

    const {
        data: contractDetails = {} as ContractDetail,
        isFetched,
        isLoading,
    } = useQuery(
        ["contract", { contractId }],
        () => contractApi.getContract(+contractId, +policyholderId, categoryCode, stateCode),
        {
            enabled: !!policyholderId,
        },
    );

    useEffect(() => {
        if (!isFetched && policyholderId === null) {
            navToContracts(ALL_CONTRACTS_ID);
        }

        if (!isEmpty(contractDetails)) {
            setPageTitle(`${contractDetails.branch.name} (${contractDetails.reference})`);
        }
    }, [isFetched]);

    let insurerLinks: InsurerLink[] = [];

    if (!isEmpty(contractDetails)) {
        insurerLinks = getInsurerLinks(contractDetails);
    }

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemEvent("contract", "Contracts");
    });

    ibpAclId = contractDetails && !policyholderId ? user.getIbpAclId(contractDetails.policyholderId) : ibpAclId;

    const allowedToSeeActivities = user.hasPermission(
        Permissions.ViewContractActivities,
        +contractDetails.policyholderId,
    );

    const handleShowMoreActivities = () => {
        navToRelatedActivities(contractId, contractDetails.title, "contract", contractDetails.policyholderId, ibpAclId);
    };

    const handleShowDocuments = () => {
        navToRelatedDocuments(contractId, contractDetails.title, "contracts");
    };

    const handleAccessConversations = () => {
        navToContextualConversation(contractId, "contract", contractDetails.title, policyholderId);
    };

    const panels = [
        {
            label: t("Common.Details"),
            content: !isLoading && <ContractTabDetail contractDetails={contractDetails} />,
        },
        {
            label: t("Details.RelatedClaims"),
            content: (
                <RelatedClaims
                    id={contractId}
                    featureType="contract"
                    ibpAclId={ibpAclId}
                    policyholderId={contractDetails.policyholderId}
                    isSchoolContract={contractDetails.isSchoolContract}
                />
            ),
        },
    ];

    return (
        <StandardLayout title={t("PageTitles.ContractDetails")}>
            <DetailsPage
                content={
                    <InsurerLinkPanel
                        insurer={contractDetails.insurer}
                        insurerLinks={insurerLinks}
                        description={t("Contracts.Details.Invocation.Description")}
                    />
                }
                panels={panels}
                objectType="contract"
                handleShowDocuments={
                    user.hasPermission(Permissions.ViewLinkedDocuments, +contractDetails.policyholderId) &&
                    handleShowDocuments
                }
                handleShowConversation={
                    user.hasPermission(Permissions.AccessConversations, +contractDetails.policyholderId) &&
                    handleAccessConversations
                }
                activities={
                    allowedToSeeActivities && (
                        <ActivitiesPanel
                            policyholderId={+contractDetails.policyholderId}
                            featureType="contract"
                            onShowMoreClick={handleShowMoreActivities}
                        />
                    )
                }
                card={!isLoading && <ContractItem contract={contractDetails} />}
            />
        </StandardLayout>
    );
};

export default ContractDetailPage;
